import { DishAllergens } from './DishAllergens'
import { useEffect, useMemo, useState } from 'react'
import { MinimalAlert, MinimalButton, MinimalDropdown, MinimalInput, MinimalNumberField } from '@ovasabi/ui-minimal'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { ChowModal } from '../../components/chow'
import { allowSeedContent, commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { confirmedUpsert, echoUpsert, type StoreRecord } from './chefShared'
import { CoverPicker, useImagePicker } from './imagePicker'
import { citiesForState, composeRegionTag, inferRegion, NIGERIAN_STATES, parseRegionTag } from './regions'
import { CategoryPicker, kitchenAddress, useKitchenCatalog } from './kitchenCatalog'
import { useDishSnapshot } from '../../generated/prototypeRuntime'
import { displayTitle } from '../../lib/displayName'

/** Case and spacing never make a different dish (mirrors the server's dishNameKey). */
const nameKey = (value: string) => value.trim().split(/\s+/).join(' ').toLowerCase()

/**
 * Edit one dish after it exists.
 *
 * A dish's photo, name and price used to be fixed at creation: the only way to
 * change any of them was to delete the dish and make a new one, which would
 * have taken its reviews and meal-plan entries with it. `menu:update_dish` is a
 * patch, so this sheet sends only what the chef actually touched — swapping a
 * photo leaves the price alone even if the price field is on screen.
 */
const CATEGORIES = [
  { value: 'main', label: 'Main' },
  { value: 'rice', label: 'Rice' },
  { value: 'soups', label: 'Soups' },
  { value: 'protein', label: 'Protein' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'grill', label: 'Grill' },
  { value: 'pastry', label: 'Pastry' },
  { value: 'drinks', label: 'Drinks' },
  { value: 'sides', label: 'Sides' },
]


const Body = styled.div`
  display: grid;
  gap: ${minimalVars.space.sm};
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${minimalVars.space.xs};
`

const Field = styled.div`
  display: grid;
  gap: 6px;
`

const FieldLabel = styled.span`
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textTertiary};
  font-weight: ${minimalVars.typography.weightSemibold};
`

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${minimalVars.space.xs};
`

export const DishEditModal = ({
  dish,
  chefProfileId,
  isOpen,
  onClose,
}: {
  dish: StoreRecord | null
  chefProfileId: string
  isOpen?: boolean
  onClose: () => void
}) => {
  const isNew = !dish || !dish.id
  const open = isOpen !== undefined ? isOpen : Boolean(dish)
  const catalog = useKitchenCatalog(chefProfileId, open)
  const currentImage = dish?.image_url ? String(dish.image_url) : undefined
  const photo = useImagePicker('dish', chefProfileId, currentImage)
  const [name, setName] = useState(String(dish?.name ?? ''))
  const [price, setPrice] = useState(String(dish?.price_minor ? Number(dish.price_minor) / 100 : ''))
  const [description, setDescription] = useState(String(dish?.description ?? ''))
  const [ingredients, setIngredients] = useState(Array.isArray(dish?.ingredients) ? dish.ingredients.join(', ') : '')
  const [cookTime, setCookTime] = useState(String(dish?.cook_time_minutes || ''))
  const [category, setCategory] = useState(String(dish?.category ?? 'main').toLowerCase())
  // Publication validates region and allergen details through the status command.
  const initialRegion = parseRegionTag(String(dish?.region_tag ?? ''))
  const [regionState, setRegionState] = useState(initialRegion.state || 'FCT')
  const [regionCity, setRegionCity] = useState(initialRegion.city || 'Abuja')
  const [regionTouched, setRegionTouched] = useState(Boolean(dish?.region_tag))
  /* A new dish is cooked where the kitchen is. Start from the saved kitchen
     location instead of the Abuja default, until the chef picks otherwise. */
  const kitchenLocation = catalog.locations[0]
  useEffect(() => {
    if (regionTouched || !kitchenLocation) return
    const inferred = inferRegion(kitchenAddress(kitchenLocation))
    setRegionState(inferred.state)
    setRegionCity(inferred.city)
  }, [kitchenLocation, regionTouched])

  /* The server refuses a second dish with the same name in the library. Say so
     while the chef is typing, not after they press Create. */
  const library = useDishSnapshot() as { records: readonly StoreRecord[] }
  const duplicate = useMemo(() => {
    const key = nameKey(name)
    if (!key) return undefined
    return library.records.find((row) =>
      String(row.chef_profile_id ?? '') === chefProfileId &&
      !String(row.menu_id ?? '') &&
      String(row.id ?? '') !== String(dish?.id ?? '') &&
      nameKey(String(row.name ?? '')) === key)
  }, [library.records, name, chefProfileId, dish?.id])
  const initialAllergens = Array.isArray(dish?.allergens) && dish.allergens.length > 0
    ? (dish?.allergens as unknown[]).map(String)
    : []
  const [allergens, setAllergens] = useState<ReadonlySet<string>>(() => new Set(initialAllergens.length > 0 ? initialAllergens : ['none']))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const cityOptions = useMemo(
    () => citiesForState(regionState).map((city) => ({ value: city, label: city })),
    [regionState],
  )
  const stateOptions = useMemo(
    () =>
      NIGERIAN_STATES.map((s) => ({ value: s.name, label: s.name === 'FCT' ? 'FCT (Abuja)' : s.name })),
    [],
  )

  if (!open) return null
  const id = String(dish?.id ?? '')
  const isHidden = String(dish?.status ?? '') === 'hidden'

  const save = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Give the dish a name.')
      return
    }
    if (duplicate) {
      setError(`“${displayTitle(duplicate.name)}” is already in your library. Edit that dish instead.`)
      return
    }
    const priceMinor = Math.round(Number(price) * 100)
    if (price.trim() === '' || !Number.isFinite(priceMinor) || priceMinor < 0) {
      setError('Enter a valid price.')
      return
    }
    // Send only what changed. The command is a patch, so an untouched field is
    // an absent key — not a resend of the same value.
    if (allergens.size === 0) { setError('Select the dish allergens, or confirm that none apply.'); return }
    const nextIngredients = ingredients.split(',').map((item) => item.trim()).filter(Boolean)
    const minutes = cookTime.trim() === '' ? 0 : Number(cookTime)
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) { setError('Cook time must be a whole number between 1 and 1440, or blank.'); return }
    const nextRegionTag = composeRegionTag(regionCity, regionState)
    const nextAllergens = [...allergens]

    if (isNew) {
      setSaving(true)
      setError('')
      try {
        const payload: StoreRecord = {
          chef_profile_id: chefProfileId,
          name: trimmed,
          category,
          price_minor: priceMinor,
          description: description.trim(),
          ingredients: nextIngredients,
          allergens: nextAllergens,
          cook_time_minutes: minutes,
          region_tag: nextRegionTag,
          image_url: photo.url || '',
          menu_id: '',
          status: 'available',
        }
        let created: StoreRecord
        try {
          const resp = await commands.menu.createDish(payload)
          created = { ...payload, ...commandPayload(resp) }
        } catch (err) {
          if (isLiveRuntime() || !allowSeedContent()) throw err
          created = { ...payload, id: `dish-${Date.now()}` }
        }
        confirmedUpsert('menu.dishes', created)
        onClose()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not create this dish.')
      } finally {
        setSaving(false)
      }
      return
    }

    const patch: Record<string, unknown> = {}
    if (description.trim() !== String(dish?.description ?? '')) patch.description = description.trim()
    if (nextIngredients.join('|') !== (Array.isArray(dish?.ingredients) ? dish.ingredients.join('|') : '')) patch.ingredients = nextIngredients
    if (minutes !== Number(dish?.cook_time_minutes ?? 0)) patch.cook_time_minutes = minutes
    if (trimmed !== String(dish?.name ?? '')) patch.name = trimmed
    if (priceMinor !== Number(dish?.price_minor ?? 0)) patch.price_minor = priceMinor
    if (category !== String(dish?.category ?? '').toLowerCase()) patch.category = category
    if (photo.changed && photo.url) patch.image_url = photo.url

    // Publish-gate fields. Send when they changed, so supplying them un-hides a
    // dish (the backend recomputes visibility from region_tag + allergens).
    if (nextRegionTag !== String(dish?.region_tag ?? '')) patch.region_tag = nextRegionTag
    if (nextAllergens.join('|') !== initialAllergens.join('|')) patch.allergens = nextAllergens

    if (Object.keys(patch).length === 0) {
      onClose()
      return
    }

    setSaving(true)
    setError('')
    try {
      const response = await commands.menu.updateDish(id, patch)
      const saved = commandPayload(response)
      confirmedUpsert('menu.dishes', { ...dish, ...patch, ...saved })
      if (Array.isArray(saved.updated_dishes)) for (const copy of saved.updated_dishes) confirmedUpsert('menu.dishes', copy as StoreRecord)
      onClose()
    } catch (err) {
      if (isLiveRuntime() || !allowSeedContent()) {
        setError(err instanceof Error ? err.message : 'Could not save this dish.')
      } else {
        // No backend to talk to: keep the local projection honest and close.
        echoUpsert('menu.dishes', { ...dish, ...patch })
        onClose()
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <ChowModal open={open} title={isNew ? 'New dish' : 'Edit dish'} onClose={onClose}>
      <Body>
        <MinimalAlert tone="info">
          {isNew
            ? 'Add a dish to your kitchen library. You can include it on any menu.'
            : 'Changes show everywhere this dish appears. Orders already placed keep their original price.'}
        </MinimalAlert>
        <CoverPicker
          picker={photo}
          ratio="16 / 9"
          size={320}
          label="Add photo"
          glyph="🍽️"
          helper="A clear photo of the plated dish, in daylight if you can"
        />
        <MinimalInput
          required
          label="Name"
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          hint={name.trim() && displayTitle(name) !== name.trim() ? `Shows as “${displayTitle(name)}”` : 'For example, Jollof Rice with Chicken'}
          error={duplicate ? `You already have “${displayTitle(duplicate.name)}”. Edit that dish instead.` : undefined}
        />
        <MinimalInput
          label="Price (₦)" required hint="What the customer pays, in naira, including any VAT you charge. ChooseChow adds only its service fee." type="number" min="0" step="0.01"
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <CategoryPicker
          options={catalog.categories.length ? catalog.categories : CATEGORIES}
          value={category}
          onChange={setCategory}
        />

        <MinimalInput label="Description" value={description} onChange={(event) => setDescription(event.target.value)} />
        <MinimalInput label="Main ingredients" value={ingredients} onChange={(event) => setIngredients(event.target.value)} />
        <MinimalNumberField label="Cook time (min)" min={1} max={1440} step={1} value={cookTime === '' ? null : Number(cookTime)} onValueChange={(value) => setCookTime(value == null ? '' : String(value))} />
        <DishAllergens value={allergens} onChange={setAllergens} />
        {isHidden && (
          <MinimalAlert tone="warning" title="Hidden from customers">
            This dish is hidden. Save its details, then select Publish dish in your library.
          </MinimalAlert>
        )}

        <Field>
          <FieldLabel>Where it’s cooked (required to publish)</FieldLabel>
          <TwoCol>
            <MinimalDropdown
              label={<>State <span aria-hidden="true">*</span></>}
              options={stateOptions}
              value={regionState}
              onChange={(next) => {
                setRegionTouched(true)
                setRegionState(next)
                setRegionCity(citiesForState(next)[0] ?? '')
              }}
            />
            <MinimalDropdown
              label={<>City / area <span aria-hidden="true">*</span></>}
              options={cityOptions}
              value={regionCity}
              onChange={(next) => { setRegionTouched(true); setRegionCity(next) }}
            />
          </TwoCol>
        </Field>

        {error ? (
          <MinimalAlert tone="danger" title="Dish not saved">
            {error}
          </MinimalAlert>
        ) : null}
        <Actions>
          <MinimalButton variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </MinimalButton>
          <MinimalButton onClick={save} loading={saving} disabled={photo.uploading || Boolean(duplicate)}>
            {isNew ? 'Create dish' : 'Save dish'}
          </MinimalButton>
        </Actions>
      </Body>
    </ChowModal>
  )
}
